import BrandMark from "../components/brand/BrandMark";
import AuthPlanCard from "../components/auth/AuthPlanCard";
import { useState } from "react";
import { usePricing } from "../context/PricingContext";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { 
  Eye, EyeOff, ArrowLeft, Phone, Shield, 
  Smartphone, Bell, Lock, CheckCircle2 
} from "lucide-react";
import { useI18n } from "../i18n";

const Login = () => {

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password);
      toast.success(t("auth.welcome"));
      navigate(user.role === "admin" ? "/admin" : "/dashboard");
    } catch (error) {
      const message = error.response?.data?.detail || "Error al iniciar sesión";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    { icon: Phone, text: t("auth.benefit_calls") },
    { icon: Smartphone, text: t("auth.benefit_no_apps") },
    { icon: Bell, text: t("auth.benefit_alerts") },
    { icon: Shield, text: t("auth.benefit_protection") }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex overflow-hidden">
      {/* Left Side - Form */}
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 py-12 lg:px-16">
        {/* Back Button */}
        <Link 
          to="/" 
          className="inline-flex items-center gap-2 text-slate-500 hover:text-[#0B2545] transition-colors mb-8 w-fit"
          data-testid="login-back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>{t("auth.back")}</span>
        </Link>

        {/* Logo */}
        <div className="flex items-center gap-3 mb-8">
          <BrandMark size="md" onDark asLink={false} />
        </div>

        {/* Form Card with Glassmorphism */}
        <div className="bg-slate-100 backdrop-blur-xl rounded-2xl p-8 border border-slate-300 shadow-2xl animate-fade-in">
          {/* iter71 1.4 — sin sesión + permiso de notificaciones concedido:
              este teléfono está mudo hasta reactivar con login */}
          {typeof Notification !== "undefined" && Notification.permission === "granted" && (
            <div
              className="mb-5 p-4 rounded-xl bg-red-600 border-2 border-red-400 text-white text-sm font-bold text-center shadow-lg shadow-red-900/40"
              data-testid="relogin-push-warning"
            >
              🔔 Inicia sesión para reactivar tus avisos
              <span className="block mt-1 font-normal text-red-700 text-xs">
                Este teléfono no recibirá alertas ni llamadas de tu vehículo hasta que entres.
              </span>
            </div>
          )}
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-[#0B2545] mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              {t("auth.login_title")}
            </h1>
            <p className="text-slate-500">
              {t("auth.login_subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-600 text-sm font-medium">
                {t("auth.email")}
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="tu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl"
                autoComplete="username"
                inputMode="email"
                data-testid="login-email-input"
              />
            </div>

            {/* Password */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-600 text-sm font-medium">
                {t("auth.password")}
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="h-12 bg-white border-slate-200 text-[#0B2545] placeholder:text-slate-400 focus:border-[#1B84FF] focus:ring-[#1B84FF]/20 rounded-xl pr-12"
                  autoComplete="current-password"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-[#0B2545] transition-colors"
                  aria-label={showPassword ? t("auth.hide_password") : t("auth.show_password")}
                  aria-pressed={showPassword}
                  data-testid="login-toggle-password"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="text-right">
              <Link 
                to="/forgot-password" 
                className="text-sm text-slate-500 hover:text-[#1B84FF] transition-colors"
                data-testid="forgot-password-link"
              >
                {t("auth.forgot")}
              </Link>
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              className="w-full h-12 rounded-xl bg-[#1B84FF] hover:bg-[#0F6FDB] text-white font-semibold shadow-lg shadow-[#1B84FF]/30 transition-all hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  {t("auth.logging_in")}
                </span>
              ) : t("auth.login_btn")}
            </Button>
          </form>

          {/* Register Link */}
          <div className="mt-6 text-center">
            <p className="text-slate-500">
              {t("auth.no_account")}{" "}
              <Link 
                to="/register" 
                className="text-[#1B84FF] hover:text-[#0F6FDB] font-semibold transition-colors"
                data-testid="login-register-link"
              >
                {t("auth.register_here")}
              </Link>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Benefits & Image (Desktop only) */}
      <div className="hidden lg:flex w-1/2 flex-col justify-center items-center p-12 relative">
        {/* Decorative gradients */}
        <div className="absolute top-20 right-20 w-64 h-64 bg-[#1B84FF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-48 h-48 bg-blue-500/20 rounded-full blur-3xl"></div>

        {/* Benefits Card */}
        <div className="bg-white backdrop-blur-lg rounded-2xl p-8 border border-slate-200 max-w-md z-10">
          <h2 className="text-2xl font-bold text-[#0B2545] mb-6" style={{ fontFamily: 'Manrope, sans-serif' }}>
            {t("auth.why_avisocar")}
          </h2>
          
          <div className="space-y-4">
            {benefits.map((benefit, index) => (
              <div 
                key={index} 
                className="flex items-center gap-4 animate-fade-in"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="w-10 h-10 bg-[#1B84FF]/20 rounded-full flex items-center justify-center flex-shrink-0">
                  <benefit.icon className="w-5 h-5 text-[#1B84FF]" />
                </div>
                <span className="text-[#0B2545] font-medium">{benefit.text}</span>
                <CheckCircle2 className="w-5 h-5 text-[#1B84FF] ml-auto" />
              </div>
            ))}
          </div>

          {/* RC-WEB-2 — plan por defecto publicado (sin duración hardcodeada) */}
          <AuthPlanCard testId="login-price" />
        </div>

        {/* Sticker Preview Image */}
        <div className="mt-8 relative z-10">
          <img 
            src="https://customer-assets.emergentagent.com/job_canva-stickers/artifacts/27dohpu7_image.png"
            alt="AvisoCar Sticker Preview"
            className="w-80 rounded-2xl shadow-2xl opacity-80 hover:opacity-100 transition-opacity"
          />
        </div>
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fade-in {
          animation: fade-in 0.5s ease-out forwards;
        }
      `}</style>
    </div>
  );
};

export default Login;
